import { Recipe, RecipeStep } from '../models/Recipe';
import { v4 as uuidv4 } from 'uuid';
import openAIService from './openaiService';
import { getApiKey } from './authService';
import { logger } from '../utils/logger';
import { AgentProgressCallback } from '../types/agentTypes';
import {
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  QueryDocumentSnapshot,
  Timestamp
} from 'firebase/firestore';
import {
  getUserRecipesCollection,
  getRecipeDocRef,
  serverTimestamp
} from '../utils/firestoreHelpers';

/**
 * Service for managing recipes with Firestore
 */
class RecipeService {
  /**
   * Toggle favorite status for a recipe
   * Now updates the isFavorite field directly in Firestore
   *
   * @param userId - Firebase Auth UID
   * @param recipeId - Recipe document ID
   * @param currentStatus - Current favorite status
   * @returns Promise resolving to new favorite status
   */
  async toggleFavorite(userId: string, recipeId: string, currentStatus: boolean): Promise<boolean> {
    try {
      const docRef = getRecipeDocRef(userId, recipeId);
      const newStatus = !currentStatus;

      await updateDoc(docRef, {
        isFavorite: newStatus,
        updatedAt: serverTimestamp()
      });

      logger.debug(`Recipe ${recipeId} favorite status: ${newStatus}`);
      return newStatus;

    } catch (error) {
      logger.error('Error toggling favorite status', error as Error);
      throw new Error('Failed to update favorite. Please try again.');
    }
  }

  /**
   * Get recipes with pagination and sorting using Firestore
   *
   * @param userId - Firebase Auth UID
   * @param page - Page number (1-based, for backward compatibility)
   * @param limit - Number of recipes per page
   * @param sortBy - Field to sort by ('title', 'createdAt')
   * @param sortOrder - Order to sort ('asc', 'desc')
   * @param searchQuery - Search term (applied client-side)
   * @param onlyFavorites - Filter to only favorites
   * @param lastVisibleDoc - Cursor for pagination (optional)
   * @returns Promise resolving to object with recipes, total count, and lastDoc cursor
   */
  async getRecipes(
    userId: string,
    _page: number = 1,
    limit: number = 12,
    sortBy: 'title' | 'createdAt' = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    searchQuery: string = '',
    onlyFavorites: boolean = false,
    lastVisibleDoc?: QueryDocumentSnapshot
  ): Promise<{ recipes: Recipe[]; total: number; lastDoc: QueryDocumentSnapshot | null }> {
    try {
      const recipesRef = getUserRecipesCollection(userId);

      // Build query
      let q = query(recipesRef);

      // Filter by favorites
      if (onlyFavorites) {
        q = query(q, where('isFavorite', '==', true));
      }

      // Sort (Firestore requires orderBy before limit/startAfter)
      q = query(q, orderBy(sortBy, sortOrder));

      // Pagination cursor
      if (lastVisibleDoc) {
        q = query(q, startAfter(lastVisibleDoc));
      }

      // Limit results
      q = query(q, firestoreLimit(limit));

      // Execute query
      const snapshot = await getDocs(q);

      // Convert to Recipe objects
      const recipes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Recipe[];

      // Client-side search filter (Firestore limitation)
      let filteredRecipes = recipes;
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        filteredRecipes = recipes.filter(r =>
          r.title.toLowerCase().includes(lowerQuery) ||
          r.ingredients.toLowerCase().includes(lowerQuery) ||
          (r.notes && r.notes.toLowerCase().includes(lowerQuery))
        );
      }

      // Get total count (separate query for accuracy)
      const countSnapshot = await getDocs(query(recipesRef));
      const total = countSnapshot.size;

      return {
        recipes: filteredRecipes,
        total,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
      };

    } catch (error) {
      logger.error('Error fetching recipes from Firestore', error as Error);
      throw new Error('Failed to load recipes. Please try again.');
    }
  }

  /**
   * Get recipe by ID from Firestore
   *
   * @param userId - Firebase Auth UID
   * @param recipeId - Recipe document ID
   * @returns Promise resolving to recipe or null if not found
   */
  async getRecipeById(userId: string, recipeId: string): Promise<Recipe | null> {
    try {
      const docRef = getRecipeDocRef(userId, recipeId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        logger.warn(`Recipe ${recipeId} not found for user ${userId}`);
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Recipe;

    } catch (error) {
      logger.error('Error fetching recipe by ID', error as Error);
      throw new Error('Failed to load recipe. Please try again.');
    }
  }

  /**
   * Add a new recipe to Firestore
   *
   * @param userId - Firebase Auth UID
   * @param recipe - Recipe data (without id, userId, timestamps)
   * @returns Promise resolving to added recipe with all fields
   */
  /**
   * Helper function to remove undefined fields from objects
   * Firestore doesn't accept undefined values, only null or omitted fields
   */
  private removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== undefined)
    ) as Partial<T>;
  }

  async addRecipe(
    userId: string,
    recipe: Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>
  ): Promise<Recipe> {
    try {
      const recipeId = uuidv4();  // Generate UUID client-side
      const docRef = getRecipeDocRef(userId, recipeId);

      // Strip audio data from steps before saving (audio is frontend-only)
      const recipeToSave = {
        ...recipe,
        steps: recipe.steps?.map(step => {
          const { audio, ...stepWithoutAudio } = step;
          return stepWithoutAudio;
        })
      };

      // Filter out undefined fields before saving to Firestore
      const newRecipeData = this.removeUndefinedFields({
        ...recipeToSave,
        id: recipeId,
        userId,
        isFavorite: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Use setDoc with custom ID instead of addDoc
      await setDoc(docRef, newRecipeData);

      logger.debug('Recipe created successfully', recipeId);

      // Return with client-side timestamp for immediate display
      return {
        id: recipeId,
        userId,
        isFavorite: false,
        title: recipe.title,
        ingredients: recipe.ingredients,
        notes: recipe.notes,
        steps: recipe.steps,
        image: recipe.image,
        analysis: recipe.analysis,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

    } catch (error) {
      logger.error('Error adding recipe to Firestore', error as Error);
      throw new Error('Failed to create recipe. Please try again.');
    }
  }

  /**
   * Update an existing recipe in Firestore
   *
   * @param userId - Firebase Auth UID
   * @param recipe - Recipe with all fields including id
   * @returns Promise resolving to updated recipe
   */
  async updateRecipe(userId: string, recipe: Recipe): Promise<Recipe> {
    try {
      const docRef = getRecipeDocRef(userId, recipe.id);

      // Remove id, userId, and createdAt from update payload (immutable)
      const { id, userId: _, createdAt, ...updateData } = recipe;

      // Strip audio data from steps before saving (audio is frontend-only)
      // This prevents hitting Firestore document size limits
      const dataToSave = {
        ...updateData,
        steps: updateData.steps?.map(step => {
          const { audio, ...stepWithoutAudio } = step;
          return stepWithoutAudio;
        })
      };

      // Filter out undefined fields before updating Firestore
      const cleanUpdateData = this.removeUndefinedFields({
        ...dataToSave,
        updatedAt: serverTimestamp()
      });

      await updateDoc(docRef, cleanUpdateData);

      logger.debug('Recipe updated successfully', recipe.id);

      // Return with optimistic timestamp (preserve audio in returned recipe)
      return {
        ...recipe,
        updatedAt: Timestamp.now()
      };

    } catch (error) {
      logger.error('Error updating recipe in Firestore', error as Error);
      throw new Error('Failed to update recipe. Please try again.');
    }
  }

  /**
   * Delete a recipe from Firestore
   *
   * @param userId - Firebase Auth UID
   * @param recipeId - Recipe document ID
   * @returns Promise resolving to boolean indicating success
   */
  async deleteRecipe(userId: string, recipeId: string): Promise<boolean> {
    try {
      const docRef = getRecipeDocRef(userId, recipeId);
      await deleteDoc(docRef);

      logger.debug('Recipe deleted successfully', recipeId);
      return true;

    } catch (error) {
      logger.error('Error deleting recipe from Firestore', error as Error);
      throw new Error('Failed to delete recipe. Please try again.');
    }
  }

  /**
   * Generate image for a recipe step
   * @param step Recipe step
   * @param recipeTitle Recipe title for context
   * @returns Promise resolving to step with base64 image data
   */
  async generateStepImage(step: RecipeStep, recipeTitle: string): Promise<RecipeStep> {
    try {
      // Get Green Tokens API key from Firebase
      const greenTokensApiKey = await getApiKey();
      openAIService.setApiKey(greenTokensApiKey);
      
      let imageBase64: string | null = null;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries && !imageBase64) {
        try {
          // Generate image prompt - simplify on retry
          let imagePrompt: string;
          
          if (retryCount === 0) {
            // First attempt - detailed prompt
            imagePrompt = `
              Create a photorealistic image of the following cooking step for ${recipeTitle}:
              ${step.content}
              The image should be clear, well-lit, and show the cooking process in detail.
              Style: Food photography, professional, appetizing, instructional
            `;
          } else if (retryCount === 1) {
            // Second attempt - simplified prompt
            imagePrompt = `Professional food photography of: ${step.content.substring(0, 100)}. Clean, well-lit kitchen setting.`;
          } else {
            // Final attempt - very generic prompt
            imagePrompt = `Cooking process in a kitchen. Professional food photography style.`;
          }
          
          // Generate image using OpenAI DALL-E
          imageBase64 = await openAIService.generateImage(imagePrompt);
          
        } catch (imageError: any) {
          logger.debug(`Image generation attempt ${retryCount + 1} failed`);

          // Check if it's a safety system rejection
          if (imageError.message?.includes('safety system') || imageError.status === 400) {
            retryCount++;
            if (retryCount > maxRetries) {
              logger.debug('Max retries reached for image generation');
              // Return step without image instead of throwing
              return step;
            }
            // Reduced retry delay for better performance (was 1000ms)
            await new Promise(resolve => setTimeout(resolve, 200));
          } else {
            // For other errors, throw immediately
            throw imageError;
          }
        }
      }
      
      // Update step with base64 image data if successful
      if (imageBase64) {
        const updatedStep = {
          ...step,
          image: imageBase64,
        };
        return updatedStep;
      }
      
      // Return original step if no image was generated
      return step;
    } catch (error) {
      logger.error('Error generating step image', error as Error);
      // Return step without image instead of throwing
      return step;
    }
  }

  /**
   * Generate audio for a recipe step
   * @param step Recipe step
   * @returns Promise resolving to step with base64 audio data
   */
  async generateStepAudio(step: RecipeStep): Promise<RecipeStep> {
    try {
      // Get Green Tokens API key from Firebase
      const greenTokensApiKey = await getApiKey();
      openAIService.setApiKey(greenTokensApiKey);
      
      // Generate audio using OpenAI TTS via Green Tokens proxy
      const audioBase64 = await openAIService.generateSpeech(step.content);
      
      // Update step with base64 audio data
      const updatedStep = {
        ...step,
        audio: audioBase64,
      };
      
      return updatedStep;
    } catch (error) {
      logger.error('Error generating step audio', error as Error);
      throw error;
    }
  }

  /**
   * Ask a question about a recipe step and potentially get revised steps
   * @param recipe Current recipe
   * @param stepIndex Index of the step being questioned
   * @param question User's question about the step
   * @returns Promise resolving to response and optionally revised steps
   */
  async askQuestionAboutStep(
    recipe: Recipe, 
    stepIndex: number, 
    question: string
  ): Promise<{ response: string; revisedSteps?: RecipeStep[] }> {
    try {
      // Get Green Tokens API key from Firebase
      const greenTokensApiKey = await getApiKey();
      openAIService.setApiKey(greenTokensApiKey);
      
      const currentStep = recipe.steps?.[stepIndex];
      if (!currentStep) {
        throw new Error('Invalid step index');
      }
      
      const prompt = `
        I'm cooking "${recipe.title}" and I'm on step ${stepIndex + 1}: "${currentStep.content}"
        
        Full recipe context:
        Ingredients: ${recipe.ingredients}
        Notes: ${recipe.notes}
        All steps: ${recipe.steps?.map((step, i) => `${i + 1}. ${step.content}`).join('\n')}
        
        My question: ${question}
        
        Please provide a helpful answer to my question. If my question suggests that the current step needs clarification or improvement, also provide revised cooking steps for the entire recipe.
        
        IMPORTANT: Respond with ONLY a valid JSON object, no markdown formatting or code blocks. Format your response exactly like this:
        {
          "response": "Your helpful answer to the question",
          "shouldReviseSteps": true/false,
          "revisedSteps": [
            {"content": "Step 1 description"},
            {"content": "Step 2 description"},
            ...
          ]
        }
        
        Include the "revisedSteps" array only if shouldReviseSteps is true.
      `;
      
      const aiResponse = await openAIService.getChatCompletion(prompt);
      
      // Parse the response - handle both plain JSON and markdown-wrapped JSON
      try {
        let jsonString = aiResponse.trim();
        
        // Remove markdown code blocks if present
        if (jsonString.startsWith('```json')) {
          jsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '');
        } else if (jsonString.startsWith('```')) {
          jsonString = jsonString.replace(/^```\s*/, '').replace(/\s*```$/, '');
        }
        
        logger.debug('Attempting to parse JSON from AI response');
        const parsed = JSON.parse(jsonString);

        const result: { response: string; revisedSteps?: RecipeStep[] } = {
          response: parsed.response || 'I apologize, but I couldn\'t provide a proper response to your question.'
        };

        if (parsed.shouldReviseSteps && parsed.revisedSteps && Array.isArray(parsed.revisedSteps)) {
          result.revisedSteps = parsed.revisedSteps.map((step: { content: string }) => ({
            id: uuidv4(),
            content: step.content,
          }));
          logger.debug(`Parsed ${result.revisedSteps?.length || 0} revised steps`);
        }

        return result;
      } catch (parseError) {
        logger.debug('Error parsing AI response, trying fallback');
        logger.debug('Parse error details:', parseError);
        
        // Try to extract JSON from the response using regex as a fallback
        try {
          const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            const result: { response: string; revisedSteps?: RecipeStep[] } = {
              response: parsed.response || 'I apologize, but I couldn\'t provide a proper response to your question.'
            };
            
            if (parsed.shouldReviseSteps && parsed.revisedSteps && Array.isArray(parsed.revisedSteps)) {
              result.revisedSteps = parsed.revisedSteps.map((step: { content: string }) => ({
                id: uuidv4(),
                content: step.content,
              }));
            }
            
            return result;
          }
        } catch (regexParseError) {
          logger.debug('Regex parsing also failed');
        }
        
        // Last resort fallback: return the raw response
        return {
          response: aiResponse || 'I apologize, but I couldn\'t provide a proper response to your question.'
        };
      }
    } catch (error) {
      logger.error('Error asking question about step', error as Error);
      throw error;
    }
  }

  /**
   * Multi-turn chat for recipe/step context, with revision detection
   * @param params { recipe, stepIndex?, messages, userMessage, mode? }
   * @returns Promise resolving to { assistantText, revisedSteps? }
   */
  async sendRecipeChatMessage(params: {
    recipe: Recipe;
    stepIndex?: number;
    messages: { role: 'system' | 'user' | 'assistant'; content: string }[];
    userMessage: string;
    mode?: 'general';
    onProgress?: AgentProgressCallback;
  }): Promise<{ assistantText: string; revisedSteps?: RecipeStep[]; modifiedRecipe?: Recipe; changes?: any[] }> {
    try {
      // Get Green Tokens API key from Firebase
      const greenTokensApiKey = await getApiKey();
      openAIService.setApiKey(greenTokensApiKey);

      const { recipe, stepIndex, messages, userMessage, onProgress } = params;

      // Delegate to agent service for tool-calling support
      const agentService = (await import('./agentService')).default;
      const agentResponse = await agentService.processWithTools({
        recipe,
        stepIndex,
        messages,
        userMessage,
        onProgress,
      });

      // Return response in format compatible with existing UI
      // If there are changes, also include the modified recipe
      return {
        assistantText: agentResponse.assistantText,
        revisedSteps: agentResponse.modifiedRecipe?.steps,
        modifiedRecipe: agentResponse.modifiedRecipe || undefined,
        changes: agentResponse.changes,
      };
    } catch (error) {
      logger.error('Error in sendRecipeChatMessage', error as Error);
      throw error;
    }
  }

  /**
   * Extract recipe from image using GPT Vision
   * @param imageFile Image file containing recipe
   * @returns Promise resolving to extracted recipe
   */
  async extractRecipeFromImage(imageFile: File): Promise<Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>> {
    try {
      // Get Green Tokens API key from Firebase
      const greenTokensApiKey = await getApiKey();
      openAIService.setApiKey(greenTokensApiKey);
      
      const prompt = `
        Extract a complete recipe from this image. 
        Include the title, ingredients list, and any notes or special instructions.
        Format the response as a JSON object with the following structure:
        {
          "title": "Recipe Title",
          "ingredients": "Ingredient 1, Ingredient 2, ...",
          "notes": "Any special notes or instructions"
        }
      `;
      
      const response = await openAIService.getGPTVision(imageFile, prompt, 1000);
      
      // Parse recipe from response
      try {
        const recipe = JSON.parse(response);
        return {
          title: recipe.title || 'Untitled Recipe',
          ingredients: recipe.ingredients || '',
          notes: recipe.notes || '',
        };
      } catch (parseError) {
        logger.debug('Error parsing recipe, trying text extraction fallback');

        // Fallback: try to extract recipe from text
        const titleMatch = response.match(/title["\s:]+([^"]+)/i);
        const ingredientsMatch = response.match(/ingredients["\s:]+([^"]+)/i);
        const notesMatch = response.match(/notes["\s:]+([^"]+)/i);
        
        return {
          title: titleMatch ? titleMatch[1] : 'Untitled Recipe',
          ingredients: ingredientsMatch ? ingredientsMatch[1] : '',
          notes: notesMatch ? notesMatch[1] : '',
        };
      }
    } catch (error) {
      logger.error('Error extracting recipe from image', error as Error);
      throw error;
    }
  }
}

export default new RecipeService();
